import React, { JSX } from 'react';
import {
	BCPopover,
	BCLoading,
	BCScrollbars,
	BCButton,
	BCList,
	BCInput,
	IInputChangeEventArgs,
	findMessage
} from '@bimser/components';
import { IObjectSelectPopoverProps } from '../entities/IObjectSelectPopoverProps';
import Style from '../assets/style.scss';
import { IObjectSelectActionTypes } from '../entities/IObjectSelectActionTypes';
import { IObjectItem } from '../entities/IObjectItem';
import { DmObjectType } from '@bimser/common';
import IconBack from '@bimser/icons/16/back';
import IconClose from '@bimser/icons/16/close-window';
import IconMore from '@bimser/icons/16/more';
import ObjectItem from './ObjectItem';
import { isNullOrUndefined, debounce } from '@bimser/core';

const ObjectSelectPopover = (props: IObjectSelectPopoverProps) => {
	const defaultObject: Array<IObjectItem> = [];
	const [searchValue, setSearchValue] = React.useState('');
	const [selectedRow, setSelectedRow] = React.useState(defaultObject);
	const topRef = React.createRef<HTMLDivElement>();
	const popoverRef = React.useRef<HTMLDivElement>(null);
	const titleRef = React.useRef<HTMLDivElement>(null);

	const generatePopoverTitle = (): JSX.Element => {
		const title = props.currentFolder.folderName ? props.currentFolder.folderName : 'Root'; // getSelectedRowAsTitle(selectedRow)
		return (
			<div
				ref={titleRef}
				className={Style.ObjectSelectTitle + ' ' + Style.noselect}
			>
				{props.currentFolder && props.currentFolder.id != '#Root' && (
					<IconBack
						onClick={handleClickBack}
						color="#4a90e2"
					/>
				)}
				<span className={Style.ObjectSelectTitleText}>{title}</span>
				<IconClose
					seleniumId="dmObjectSelectCloseIcon"
					height="20px"
					width="20px"
					onClick={props.willClose}
				/>
			</div>
		);
	};

	const handleClickBack = () => {
		setSelectedRow([]);
		props.onActionReceived({
			actionType: IObjectSelectActionTypes.GoBack,
			data: { selectType: props.selectType }
		});
	};

	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(event.target as Node) &&
				titleRef.current &&
				!titleRef.current.contains(event.target as Node)
			) {
				props.willClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	React.useEffect(() => {
		props.notifyOpen({
			panelId: props.parentId,
			data: { selectType: props.selectType }
		});

		return () => {
			props.notifyClose();
		};
	}, []);

	React.useEffect(() => {
		if (searchValue.length > 0 && props.isLoading === false) {
			props.onSearch({
				id: props.currentFolder.id,
				selectType: props.selectType,
				secretKey: props.currentFolder.id != '#Root' ? props.currentFolder.secretKey : null,
				searchWord: searchValue
			});
		} else {
			props.changeLoading(false);
		}
	}, [searchValue]);

	const handleFolderItemDoubleClick = (data: IObjectItem) => {
		if (DmObjectType.File != data.type) {
			if (props.selectType.length == 1 && props.selectType[0] == DmObjectType.Repository) {
				return;
			}
			setSelectedRow([]);
			setSearchValue('');
			props.onActionReceived({
				actionType: IObjectSelectActionTypes.OpenFolder,
				data: {
					selectType: props.selectType,
					current: props.currentFolder,
					next: {
						folderName: data.name,
						secretKey: data.secretKey,
						id: data.id
					}
				}
			});
		}
	};

	const getFilteredDataSource = () =>
		props.objectList.filter(
			(item: IObjectItem) =>
				!isNullOrUndefined(item.name) &&
				item.name.toLowerCase().includes(searchValue.toLowerCase())
		);

	const handleFolderItemSelected = (data: IObjectItem) => {
		if (props.selectType && props.selectType.indexOf(data.type) > -1) {
			if (
				props.currentFolder.id != '#Root' ||
				props.selectType.indexOf(DmObjectType.Repository) > -1
			) {
				if (selectedRow.length > 0 && selectedRow.find(q => q.id == data.id) != null) {
					setSelectedRow(selectedRow.filter(item => item.id != data.id));
				} else {
					if (!props.multiple) {
						setSelectedRow([data]);
					} else {
						setSelectedRow(selectedRow.concat(data));
					}
				}
			}
		} else if (!props.selectType) {
			if (selectedRow.length > 0 && selectedRow.find(q => q.id == data.id) != null) {
				setSelectedRow(selectedRow.filter(item => item.id != data.id));
			} else {
				if (!props.multiple) {
					setSelectedRow([data]);
				} else {
					setSelectedRow(selectedRow.concat(data));
				}
			}
		} else {
			setSelectedRow([]);
		}
	};

	const renderObjectItem = (data: IObjectItem) => (
		<ObjectItem
			data={data}
			handleFolderItemSelected={handleFolderItemSelected}
			handleFolderItemDoubleClick={handleFolderItemDoubleClick}
			selected={selectedRow.length > 0 && selectedRow.findIndex(x => x.id == data.id) > -1}
		/>
	);

	const handleSearchOnChange = debounce((e: IInputChangeEventArgs) => {
		setSearchValue(e.data);
	}, 500);

	const generateListHeader = () => (
		<div
			ref={topRef}
			style={{ padding: '12px 14px' }}
		>
			<BCInput
				seleniumId="dmObjectSelectInput"
				size="small"
				placeHolder={findMessage.get('100002')}
				onChange={handleSearchOnChange}
				value={searchValue}
			/>
		</div>
	);

	const onLoadMore = () => {
		props.loadMoreFolders({
			lastIndex: props.objectList.length,
			id: props.currentFolder.id,
			selectType: props.selectType,
			secretKey: props.currentFolder.id !== '#Root' ? props.currentFolder.secretKey : null
		});
	};

	const generateLoadMoreItem = () => {
		if (searchValue.length > 0) {
			return;
		}
		if (props.objectList.length < 10) {
			return;
		}
		return (
			<div
				className={[Style.noselect, Style.ObjectItem, Style.LoadMore].join(' ')}
				onClick={onLoadMore}
			>
				<BCLoading show={props.isLoadingMore}>
					<IconMore
						width="24px"
						height="24px"
						color="#4a90e2"
					/>
					<span
						className={Style.noselect}
						style={{ marginLeft: 10 }}
					>
						{' '}
						{findMessage.get('100853')}
					</span>
				</BCLoading>
			</div>
		);
	};

	const generateObjectList = () => (
		<BCList
			dataSource={getFilteredDataSource()}
			renderItem={renderObjectItem}
			header={generateListHeader()}
			loadMore={props.currentFolder.canLoadMore && generateLoadMoreItem()}
		/>
	);

	const handleBtnClick = () => {
		props.onReturn(selectedRow);
		// props.willClose();
	};

	const generatePopoverContent = (): JSX.Element => (
		<div
			ref={popoverRef}
			className={Style.ObjectSelectContent}
		>
			<BCLoading show={props.isLoading || (searchValue.length > 0 && props.isLoadingMore)}>
				<div className={Style.ObjectSelectList}>
					<BCScrollbars styles={{ padding: 5 }}>{generateObjectList()}</BCScrollbars>
				</div>
				<div className={Style.ObjectSelectBottom}>
					{
						<BCButton
							seleniumId="dmObjectselectOkButton"
							type="primary"
							text={findMessage.get('100004')}
							onClick={handleBtnClick}
						/>
					}
				</div>
			</BCLoading>
		</div>
	);

	return (
		<BCPopover
			placement={props.placement ? props.placement : 'bottom'}
			open={props.isVisible}
			overlayClassName={Style.ObjectSelectPopoverWrapper}
			content={generatePopoverContent()}
			title={generatePopoverTitle()}
			trigger="click"
		>
			{props.children}
		</BCPopover>
	);
};

export default ObjectSelectPopover;
